import React, { useState } from 'react';
import { type DocumentElement as DocumentElementType } from '@eaco/forms-core';
import { FieldHeader, FieldErrors, FieldHelpText } from './FieldParts';
import {
  useElementValue,
  useIsEditor,
  useIsReadOnly,
} from '../DocumentView/DocumentContext';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';
import { useDefaultValue } from './useDefaultValue';

interface Props {
  element: DocumentElementType;
}

const STAR_PATH =
  'M12 2l2.9 6.3 6.9.7-5.1 4.7 1.4 6.8L12 17.8 5.9 20.5l1.4-6.8L2.2 9l6.9-.7z';

const DEFAULT_STAR_COLOUR = '#f5a623';

const toInt = (v: unknown, fallback: number): number => {
  if (v === '' || v === null || v === undefined) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n) : fallback;
};

/**
 * Rating (Elements Library): a row of stars; clicking sets the rating,
 * clicking the current value clears it. Filled stars use the gold rating
 * colour, empty stars the field border grey. Value stored as the raw
 * integer (string).
 */
const RatingInput: React.FC<Props> = ({ element }) => {
  const [value, setValue] = useElementValue<string>(element.id);
  const isEditor = useIsEditor();
  const isReadOnly = useIsReadOnly();
  const [hover, setHover] = useState(0);
  const [touched, setTouched] = useState(false);
  const data = element.data as {
    label?: string;
    subLabel?: string;
    helpText?: string;
    required?: boolean;
    hidden?: boolean;
    max?: number | string;
    colour?: string;
    defaultValue?: number | string;
    numeric?: boolean;
  };

  const label = data?.label || 'Rating';
  const subLabel = data?.subLabel;
  const helpText = data?.helpText;
  const required = !!data?.required;
  const max = Math.min(Math.max(toInt(data?.max, 5), 1), 12);
  const colour = data?.colour || DEFAULT_STAR_COLOUR;
  const numeric = !!data?.numeric;

  const defaultForHydration =
    data?.defaultValue === undefined || data?.defaultValue === null || data?.defaultValue === ''
      ? undefined
      : String(toInt(data.defaultValue, 0));
  const displayValue = useDefaultValue(value, setValue, defaultForHydration);
  const current = toInt(displayValue, 0);
  const shown = hover || current;

  const errors =
    isEditor || !touched || !required || current > 0
      ? []
      : ['This field is required'];

  const size = 30;

  return (
    <div className="space-y-2">
      <FieldHeader
        htmlFor={element.id}
        label={label}
        subLabel={subLabel}
        required={required}
      />
      <div
        className="flex gap-[6px]"
        onMouseLeave={() => setHover(0)}
        role="radiogroup"
        aria-label={label}
      >
        {Array.from({ length: max }, (_, i) => {
          const n = i + 1;
          // Stars fill cumulatively (1..n); numbers highlight only the picked value.
          const active = numeric ? n === shown : n <= shown;
          const onSelect = () => {
            if (isReadOnly) return;
            setTouched(true);
            setValue(current === n ? '' : String(n));
          };
          if (numeric) {
            return (
              <button
                key={n}
                type="button"
                disabled={isReadOnly}
                aria-label={`Rating ${n}`}
                aria-pressed={n === current}
                onMouseEnter={() => !isReadOnly && setHover(n)}
                onClick={onSelect}
                className="flex h-[34px] min-w-[34px] items-center justify-center rounded-md border px-2 text-sm font-semibold transition-colors disabled:cursor-default"
                style={{
                  borderColor: active ? colour : 'var(--forms-border)',
                  backgroundColor: active ? colour : 'transparent',
                  color: active ? '#ffffff' : 'var(--forms-muted)',
                }}
              >
                {n}
              </button>
            );
          }
          return (
            <button
              key={n}
              type="button"
              disabled={isReadOnly}
              aria-label={`${n} star${n === 1 ? '' : 's'}`}
              aria-pressed={n <= current}
              onMouseEnter={() => !isReadOnly && setHover(n)}
              onClick={onSelect}
              className="leading-none transition-transform hover:scale-110 disabled:cursor-default disabled:hover:scale-100"
              style={{ color: active ? colour : 'var(--forms-border)' }}
            >
              <svg
                width={size}
                height={size}
                viewBox="0 0 24 24"
                fill="currentColor"
                stroke="none"
                aria-hidden
              >
                <path d={STAR_PATH} />
              </svg>
            </button>
          );
        })}
      </div>
      <FieldErrors errors={errors} />
      <FieldHelpText>{helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

export default RatingInput;
